import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { checkProxyConnection } from './utils/cloudflareApi';
import { initCloudflareFromEnv } from './utils/cloudflareInit';
import { useStore } from './utils/store';
import LoginScreen from './components/LoginScreen';
import MarketingPage from './components/MarketingPage';
import SalesPage from './components/SalesPage';
import Header from './components/Header';

const App: React.FC = () => {
  const [cloudflareStatus, setCloudflareStatus] = useState<'checking' | 'configured' | 'not-configured' | 'error'>('checking');
  const [proxyStatus, setProxyStatus] = useState<'checking' | 'available' | 'unavailable'>('checking');
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  
  // Get the manually reload data function from the store
  const manuallyLoadData = useStore((state) => state.manuallyLoadData);
  
  // Initialize Cloudflare and check configuration on mount
  useEffect(() => {
    const initialize = async () => {
      console.log('🔄 App mounted, initializing Cloudflare KV...');
      setIsLoading(true);
      
      try {
        // Initialize from environment variables
        const initialized = await initCloudflareFromEnv();
        if (initialized) {
          console.log('✅ Cloudflare KV initialized successfully');
          setCloudflareStatus('configured');
        } else {
          console.error('❌ Failed to initialize Cloudflare KV');
          setCloudflareStatus('not-configured');
        }
        
        // Check if proxy server is available
        const proxyAvailable = await checkProxyConnection();
        setProxyStatus(proxyAvailable ? 'available' : 'unavailable');
        
        // If everything is ready, manually trigger data loading to ensure we have the latest data
        if (initialized && proxyAvailable) {
          console.log('🔄 Both Cloudflare KV and proxy are available, manually triggering data load');
          await manuallyLoadData();
        }
      } catch (error) {
        console.error('❌ Error during initialization:', error);
        setCloudflareStatus('error');
        setProxyStatus('unavailable');
      } finally {
        setIsLoading(false);
      }
    };

    initialize();
  }, [manuallyLoadData]);
  
  // Retry loading data
  const handleRetryLoad = async () => {
    console.log('🔄 Manually retrying data load...');
    setIsLoading(true);
    
    try {
      // Re-initialize and check status
      const initialized = await initCloudflareFromEnv();
      setCloudflareStatus(initialized ? 'configured' : 'not-configured');
      
      const proxyAvailable = await checkProxyConnection();
      setProxyStatus(proxyAvailable ? 'available' : 'unavailable');
      
      if (initialized && proxyAvailable) {
        await manuallyLoadData();
        console.log('✅ Data reload complete');
      }
    } catch (error) {
      console.error('❌ Error during retry:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle login attempt
  const handleLogin = (password: string) => {
    const correctPassword = process.env.REACT_APP_PASSWORD || '1234';
    
    if (password === correctPassword) {
      setIsAuthenticated(true);
      localStorage.setItem('is_authenticated', 'true');
    } else {
      alert('Şifre yanlış. Lütfen tekrar deneyin.');
    }
  };

  // Handle logout
  const handleLogout = () => {
    localStorage.removeItem('is_authenticated');
    setIsAuthenticated(false);
  };

  // Check if user is already authenticated on mount
  useEffect(() => {
    const isAuth = localStorage.getItem('is_authenticated') === 'true';
    setIsAuthenticated(isAuth);
  }, []);
  
  // Show login screen if not authenticated
  if (!isAuthenticated) {
    return <LoginScreen onLogin={handleLogin} />;
  }
  
  return (
    <Router>
      <div className="min-h-screen bg-gray-100">
        <Header 
          isLoading={isLoading}
          onRetryLoad={handleRetryLoad}
          onLogout={handleLogout}
          showRetryButton={!isLoading && (cloudflareStatus === 'not-configured' || proxyStatus === 'unavailable')}
        />
        
        <Routes>
          <Route path="/" element={<MarketingPage />} />
          <Route path="/sales" element={<SalesPage />} />
        </Routes>
        
        <footer className="bg-white border-t border-gray-200 mt-8">
          <div className="max-w-full mx-auto py-3 px-4 sm:px-6 lg:px-8">
            <p className="text-center text-sm text-gray-500">
              Marketing Research Tool - Built with React & TailwindCSS
            </p>
          </div>
        </footer>
      </div>
    </Router>
  );
};

export default App; 